import { useMemo, useEffect, useCallback } from 'react';

import { logger } from 'src/utils/logger';
import axios, { endpoints, refreshAccessToken } from 'src/utils/axios';

import { updateUser, useIsLogged, getAccessToken, getActiveSessionId, useUserSessionStore } from 'src/stores/user-session';

import { isValidToken } from 'src/auth/context/jwt/utils';

import { AuthContext } from '../auth-context';

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const { setReady, activeSessionId, logout } = useUserSessionStore(state => state);
  const logged = useIsLogged();

  // We can use a local state to expose the current user for the active session,
  // or just read from store. But the Context API expects specific shape.
  // Let's derive it from the store + active session.
  const sessions = useUserSessionStore(state => state.sessions);
  const user = useMemo(() => {
    return sessions.find(s => s.sessionId === activeSessionId)?.user || null;
  }, [sessions, activeSessionId]);


  const checkUserSession = useCallback(async () => {
    try {
      // 1. First, try to restore the access token via the HTTP-only refresh cookie
      // This will hydrate the in-memory token store if successful
      if (logged) {
        // If we think we are logged in (have metadata), only refresh when the persisted token is missing/expired.
        const token = getAccessToken();
        if (!isValidToken(token)) {
          await refreshAccessToken();
        }
      } else {
        // Not logged in? maybe we have a persistent cookie though?
        // We could try to "auto login" if we find a cookie even if local store is empty?
        // For now, respect local store metadata.
        setReady();
        return;
      }

      // 2. Now verify the session with the backend (and get fresh user details)
      const { data: updatedUser } = await axios.get(endpoints.me.details);

      // Refresh the stored user metadata (e.g. roles) in case it changed server-side
      // since the last login, without touching the persisted access token.
      // Read the session id live from the store rather than the closed-over
      updateUser(getActiveSessionId(), updatedUser);

    } catch (error) {
      logger.error('Session verification failed:', error);
      // If refresh or /me fails, we are not logged in.
      // Logout to clear stale metadata
      await logout();
    } finally {
      setReady();
    }
  }, [logged, setReady, logout]);

  useEffect(() => {
    checkUserSession();
    // Intentionally run only on mount: re-running on checkUserSession changes would cause loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = logged ? 'authenticated' : 'unauthenticated';

  const status = useUserSessionStore(state => state.isReady) ? checkAuthenticated : 'loading';

  const memoizedValue = useMemo(
    () => ({
      user: user,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [user, checkUserSession, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
